<script setup lang="ts">
const emit = defineEmits(['close'])
</script>

<template>
  <div class="modal-backdrop" @click.self="emit('close')">
    <div class="modal-window">
      <button class="close-btn" @click="emit('close')" aria-label="Zavřít">
        <svg
          viewBox="0 0 24 24"
          width="32"
          height="32"
          stroke="currentColor"
          stroke-width="2"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>

      <div class="modal-body">
        <div class="content-container">
          <div class="text-content">
            <h2 class="title">O projektu</h2>

            <div class="article-layout">
              <p class="description">
                <strong>Poetická mapa Prahy</strong> je otevřená výzva všem obyvatelům i
                návštěvníkům města, kteří v jeho ulicích, zákoutích a krajině nacházejí poezii.
                Cílem projektu je shromažďovat osobní postřehy o místech, která v lidech vyvolávají
                poetické obrazy, vzpomínky nebo pocity – ať už jde o výrazné lokality se silným
                geniem loci, nebo na první pohled zcela obyčejná místa, jako je alej stromů, omšelý
                dům či nenápadný výhled mezi domy.
              </p>

              <p class="description">
                Každý může do mapy přidat svůj vlastní bod. Stačí vybrat místo na mapě Prahy a
                doplnit krátký popis, který vysvětlí, proč právě toto místo působí poeticky. Popis
                má být jednoduchý, osobní a stručný – maximálně 300 znaků včetně mezer.
              </p>

              <figure class="content-image">
                <img
                  src="../assets/images/kde_je_poezie02.webp"
                  alt="Ilustrační snímek Kde je Poezie v Praze"
                />
                <figcaption>Ilustrační snímek "Kde je poezie?"</figcaption>
              </figure>

              <p class="description">
                Na začátku roku 2026 byla básnířkou města Prahy zvolena Anna Beata Háblová, která se
                rozhodla během svého funkčního období vytvořit právě tuto Poetickou mapu. Projekt je
                koncipován jako kolektivní dílo: jednotlivé krátké popisy mohou společně vytvořit
                mnohovrstevnatou báseň o Praze. Nebo vznikne něco úplně jiného – a právě to je
                součástí dalšího zkoumání.
              </p>

              <p class="description">
                Poetická mapa Prahy je tedy nejen sbírkou míst, ale i živým prostorem, kde se
                setkávají osobní příběhy, nálady a obrazy. Je pozváním k tomu dívat se na město
                jinak, citlivěji a s větší pozorností k detailům, které často míjíme.
              </p>

              <div class="split-row">
                <div class="split-text">
                  <p class="description">
                    <strong>Anna Beata Háblová</strong> je architektka a spisovatelka, která
                    dlouhodobě propojuje svět města a poezie. Ve své tvorbě se věnuje proměnám
                    veřejného prostoru, vztahu lidí k místu i jemným vrstvám městské každodennosti.
                    Je autorkou několika básnických sbírek, románů i odborných a popularizačních
                    textů o architektuře a urbanismu.
                  </p>
                  <p class="description">
                    Aplikace Praha poetická je vytvořena ve spolupráci s Institutem plánování a
                    rozvoje hl. m. Prahy a Praha město literatury.
                  </p>
                </div>

                <figure class="split-image">
                  <img
                    src="../assets/images/Ing._arch._Anna_Háblová,_Ph.D._(2022).webp"
                    alt="Anna Beata Háblová"
                  />
                  <figcaption>Anna Beata Háblová</figcaption>
                </figure>
              </div>
            </div>
          </div>

          <div class="footer-banner">
            <div class="logo-item">
              <img
                src="../assets/logos/MKP_3_RADEK_BILA+CERVENA.svg"
                alt="Městská knihovna v Praze"
                class="partner-logo"
              />
            </div>

            <div class="logo-item">
              <img src="../assets/logos/IPR_logo_1.svg" alt="IPR Praha" class="partner-logo" />
            </div>

            <div class="logo-item">
              <img
                src="../assets/logos/logo PML4_na_cerne.svg"
                alt="Praha město literatury"
                class="partner-logo"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:ital,wght@0,400;0,500;0,700;1,400&family=Newsreader:opsz,wght@6..72,400;6..72,500;6..72,700&display=swap');

.modal-backdrop {
  position: fixed;
  top: clamp(40px, 4.5vw, 50px);
  left: 0;
  width: 100vw;
  height: calc(100vh - clamp(40px, 4.5vw, 50px));
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 5;
}

.modal-window {
  background-color: #fdfbf7;
  width: 90vw;
  max-width: 1200px;
  height: auto;
  max-height: 95%;
  border-radius: 4px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: #1a2432;
}

.close-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  color: #1a2432;
  cursor: pointer;
  z-index: 10;
  padding: 5px;
  transition: transform 0.2s ease;
}

.close-btn:hover {
  transform: scale(1.15);
  color: #f2a900;
}

/* Modal body now spans full width to allow edge-to-edge footer */
.modal-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 80px 0 0 0; /* Top padding keeps content below the close button */
  overflow: hidden;
}

.content-container {
  flex: 1;
  width: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* Modified Custom Scrollbar */
.content-container::-webkit-scrollbar {
  width: 14px; /* Wider track to allow for borders */
}
.content-container::-webkit-scrollbar-track {
  background: transparent;
}
.content-container::-webkit-scrollbar-thumb {
  background-color: #1a2432;
  border-radius: 10px;
  /* Trick to indent the scrollbar thumb from the edge while keeping the container full width */
  border: 4px solid #fdfbf7;
  background-clip: padding-box;
}
.content-container::-webkit-scrollbar-thumb:hover {
  background-color: #333;
}

.text-content {
  width: 100%;
  max-width: 1000px;
  padding: 0 4rem 4rem 4rem; /* Horizontal and bottom padding applied to the text wrapper */
  box-sizing: border-box;
}

.title {
  font-family: 'Newsreader', serif;
  font-weight: 500;
  font-size: 2.2rem;
  margin: 0 0 2rem 0;
  text-align: center;
}

/* ========================================= */
/* Editorial Layout Styles                   */
/* ========================================= */

.article-layout {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.description {
  font-family: 'Newsreader', serif;
  font-weight: 400;
  font-size: 1.25rem;
  line-height: 1.6;
  text-align: justify;
  margin: 0;
}

.content-image {
  margin: 1.5rem 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.content-image figcaption {
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-style: italic;
  font-size: 0.85rem;
  color: #1a2432;
  margin-top: 0.5rem;
  text-align: center;
  width: 100%;
}

.split-row {
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 3rem;
  align-items: start;
  margin: 1.5rem 0;
}

.split-text {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.split-image {
  margin: 0;
  display: flex;
  flex-direction: column;
}

.split-image img,
.content-image img,
.image-placeholder {
  width: 100%;
  height: auto;
  border-radius: 2px;
  display: block;
}

.wide-placeholder {
  aspect-ratio: 16 / 9;
  background-color: #d9d9d9;
}

.portrait-placeholder {
  aspect-ratio: 3 / 4;
  background-color: #d9d9d9;
}

.split-image figcaption {
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-style: italic;
  font-size: 0.85rem;
  color: #1a2432;
  margin-top: 0.5rem;
  text-align: left;
}

/* ========================================= */
/* Webpage-style Footer                      */
/* ========================================= */

.footer-banner {
  background-color: #f2a900;
  min-height: 80px;
  width: 100%; /* Spans the entire scrolling container */
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 3rem;
  box-sizing: border-box;
  font-family: 'Inter', sans-serif;
  margin-top: auto; /* Pushes to bottom if text is very short */
}

.logo-item {
  display: flex;
  align-items: center;
}

.partner-logo {
  height: 48px;
  width: auto;
  object-fit: contain;
  display: block;
}

/* Mobile Responsiveness */
@media (max-width: 900px) {
  .split-row {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
}

@media (max-width: 768px) {
  .modal-body {
    padding: 60px 0 0 0;
  }
  .text-content {
    padding: 0 1.5rem 3rem 1.5rem;
  }
  .title {
    font-size: 1.8rem;
  }
  .description {
    font-size: 1.1rem;
  }
  .footer-banner {
    flex-wrap: wrap;
    padding: 1rem;
    gap: 1rem;
    justify-content: center;
  }
}
</style>
